import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { UserRoleLabels } from '../../../utils/enums';

export default function WorkOrderCommentItem({ comment, currentUser, onEdit, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.comment);

  const isOwnComment = currentUser && comment.userId === currentUser.id;
  const canEdit = isOwnComment || currentUser?.role === 'ADMIN';

  const handleSaveEdit = () => {
    if (editedComment.trim() && editedComment !== comment.comment) {
      onEdit(comment.id, editedComment);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedComment(comment.comment);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      onDelete(comment.id);
    }
  };

  return (
    <div className={`border-l-4 pl-4 py-3 mb-4 ${
      comment.isEscalation 
        ? 'border-orange-500 bg-orange-50' 
        : 'border-gray-200 bg-white'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* User Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
            {comment.user?.firstName?.[0]?.toUpperCase() || comment.user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          
          {/* User Info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">
                {comment.user?.firstName} {comment.user?.lastName}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {UserRoleLabels[comment.user?.role] || comment.user?.role}
              </span>
              {comment.isEscalation && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Urgent
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              {comment.editedAt && ' (edited)'}
            </div>
          </div>
        </div>

        {/* Actions */}
        {canEdit && !isEditing && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Comment Content */}
      {isEditing ? (
        <div className="mt-2">
          <textarea
            value={editedComment}
            onChange={(e) => setEditedComment(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSaveEdit}
              disabled={!editedComment.trim()}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-800 whitespace-pre-wrap">{comment.comment}</p>
          
          {/* Attachments */}
          {comment.attachments && comment.attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {comment.attachments.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-colors"
                >
                  <img
                    src={url}
                    alt={`Attachment ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
